/**
 * PerformanceTargetsTab — Tab content for broker performance targets
 *
 * Rendered inside BrokerCommissionSettingsPage under the "Performance Targets"
 * tab. Reads selectedBrokerDocId from the parent so a single broker selector
 * drives both tabs (commission + targets). No broker selector of its own, no
 * PageHeader — the parent page owns both.
 *
 * Replaces the standalone BrokerPerformanceTargetsPage route (2026-04-18).
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Target,
  DollarSign,
  TrendingUp,
  Activity,
  Plus,
  X,
  Edit2,
  CheckCircle2,
  Loader2,
  Vault,
  Award,
  AlertTriangle,
  RefreshCw,
  HandCoins,
  Handshake,
  CalendarClock,
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import { formatCompactCurrency, formatCurrency } from '../../utils/formatters';
import { useBrokerTargets } from '../../hooks/useBrokerTargets';
import { useBrokerTargetHistory } from '../../hooks/useBrokerTargetHistory';
import TargetHistorySection from './TargetHistorySection';
import {
  addMonths,
  brisbaneMonthNow,
  monthLong,
  monthOptions,
  periodClosed,
  shortPeriodLabel,
  trendBars,
} from './targetHistoryUtils';
import type { PerformanceTarget, CommissionComponent, BonusTier } from '../../hooks/useBrokerTargets';
import { functions } from '../../lib/firebase';
import { resolveLoanBookSelection } from '../../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** One compact rule app-wide (three significant figures); see formatters.ts. */
const formatLargeAmount = (cents: number): string => formatCompactCurrency(cents);

function formatCurrencyWithCents(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// "2026-05" → "May 2026". Undefined / malformed returns empty string so the
// caller can conditionally render.
function formatEffectiveMonth(ym?: string): string {
  if (!ym || !/^\d{4}-(0[1-9]|1[0-2])$/.test(ym)) return '';
  const [y, m] = ym.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return date.toLocaleString('en-AU', { month: 'short', year: 'numeric' });
}

const TARGET_TYPE_LABELS: Record<string, string> = {
  loan_book_value: 'Loan Book Value',
  settlement_volume: 'Settlement Volume',
  commission_earned: 'Commission Earned',
  // Legacy labels — retained for historical docs that haven't been edited yet
  upfront_commission: 'Upfront Commission',
  trail_commission: 'Trail Commission',
};

const TARGET_TYPE_HINTS: Record<string, string> = {
  loan_book_value: 'Maintain active loan book above threshold (point-in-time snapshot)',
  settlement_volume: 'Settle new loans with total value above threshold in a period',
  commission_earned:
    'Earn gross commission above threshold in a period. Choose upfront, trail, or both. Pre broker-split; deductions net into the sum.',
};

/**
 * Renders a human-readable subtitle for commission targets including their
 * component selection. Returns null for non-commission targets.
 */
function commissionSubtitle(components?: CommissionComponent[]): string | null {
  if (!components || components.length === 0) return null;
  const upfront = components.includes('upfront');
  const trail = components.includes('trail');
  if (upfront && trail) return 'Upfront + Trail';
  if (upfront) return 'Upfront only';
  if (trail) return 'Trail only';
  return null;
}

const PERIOD_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
};

// ─── Stat Card ──────────────────────────────────────────────────────────────

const ACCENT_CLASSES: Record<string, string> = {
  blue: 'bg-gradient-to-r from-transparent via-blue-500 to-transparent',
  green: 'bg-gradient-to-r from-transparent via-green-500 to-transparent',
  purple: 'bg-gradient-to-r from-transparent via-purple-500 to-transparent',
  amber: 'bg-gradient-to-r from-transparent via-amber-500 to-transparent',
};

function StatCard({
  label,
  value,
  accent,
  detail,
  valueColor,
  icon,
  iconBg,
  iconColor,
}: {
  label: string;
  value: string;
  accent: string;
  detail: string;
  valueColor?: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div
      className="rounded-lg shadow-xl overflow-hidden relative"
      style={{
        background:
          'linear-gradient(135deg, rgba(31,41,55,0.8) 0%, rgba(31,41,55,0.95) 50%, rgba(17,24,39,1) 100%)',
      }}
    >
      <div
        className={`absolute top-0 left-0 right-0 h-[2px] ${ACCENT_CLASSES[accent] || ACCENT_CLASSES.blue}`}
      />
      <div className="p-4 md:p-5 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-400 text-xs font-medium uppercase tracking-wider mb-1">
              {label}
            </p>
            <p className={`text-2xl md:text-3xl font-bold ${valueColor || 'text-white'}`}>
              {value}
            </p>
            <p className="text-xs text-gray-500 mt-1.5">{detail}</p>
          </div>
          <div
            className="p-2.5 rounded-xl"
            style={{
              background: `linear-gradient(135deg, ${iconBg}, ${iconBg.replace('0.3', '0.1')})`,
              color: iconColor,
            }}
          >
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Add/Edit Target Modal ──────────────────────────────────────────────────

interface TargetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    targetId?: string;
    type: 'loan_book_value' | 'settlement_volume' | 'commission_earned';
    label: string;
    /** Phase 6 — scaled bonus tiers (sorted ascending by threshold).
     *  Backend derives legacy thresholdCents/bonusCents from tiers[0]/tiers[last]. */
    bonusTiers: BonusTier[];
    period: 'monthly' | 'quarterly';
    commissionComponents?: CommissionComponent[];
    loanBookId?: string;
    loanBookTemplateId?: string;
    loanBookEntityCode?: string;
    loanBookLabel?: string;
    assistantUserId?: string;
    assistantDisplayName?: string;
    analystId?: string;
    analystDisplayName?: string;
    effectiveFromMonth: string;
  }) => Promise<void>;
  editingTarget?: PerformanceTarget | null;
  loanBooks: ResolvedLoanBook[];
  /** Assistants linked to the selected broker (eligible for target binding).
   *  Empty when the broker has no linked assistants. */
  assistants: Array<{ id: string; displayName: string; sharePct: number }>;
  /** Credit analysts allocated to the selected broker (PR 1A-v3). Each has
   *  separate upfront/trail percentages so the picker label can preview the
   *  composite share. */
  analysts: Array<{
    id: string;
    displayName: string;
    upfrontSharePct: number;
    trailSharePct: number;
  }>;
}

interface TierFormRow {
  thresholdDollars: string;
  bonusDollars: string;
}

type TargetTypeOption = 'loan_book_value' | 'settlement_volume' | 'commission_earned';

function TargetModal({ isOpen, onClose, onSave, editingTarget, loanBooks, assistants, analysts }: TargetModalProps) {
  const [type, setType] = useState<TargetTypeOption>('loan_book_value');
  const [label, setLabel] = useState('');
  // Phase 6: scaled bonus tiers. Always at least one row; "+ Add tier" appends.
  const [tiers, setTiers] = useState<TierFormRow[]>([
    { thresholdDollars: '', bonusDollars: '' },
  ]);
  const [period, setPeriod] = useState<'monthly' | 'quarterly'>('monthly');
  const [includeUpfront, setIncludeUpfront] = useState(true);
  const [includeTrail, setIncludeTrail] = useState(true);
  const [selectedLoanBookId, setSelectedLoanBookId] = useState<string>('all');
  // PR 1A-v3: target subject is one of broker / assistant / analyst.
  // The two id state slots remain mutually exclusive — backend rejects both.
  // Encoded as "" | "assistant:<uid>" | "analyst:<id>" so we keep ONE select
  // control in the UI (simpler than two parallel dropdowns).
  const [selectedAssistantUserId, setSelectedAssistantUserId] = useState<string>('');
  const [selectedAnalystId, setSelectedAnalystId] = useState<string>('');
  // Default effective-from = current month. Admin can pick a future month
  // to pre-schedule, but not historical months (enforced via min attr).
  const currentMonthStr = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [effectiveFromMonth, setEffectiveFromMonth] = useState(currentMonthStr);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (editingTarget) {
        const normalizedType: TargetTypeOption =
          editingTarget.type === 'loan_book_value' ||
          editingTarget.type === 'settlement_volume'
            ? editingTarget.type
            : 'commission_earned';
        setType(normalizedType);
        setLabel(editingTarget.label);
        // Hydrate tiers from the editing target. The hook's snapshot mapper
        // guarantees bonusTiers is populated (legacy single-bonus targets
        // surface as a one-element array).
        setTiers(
          editingTarget.bonusTiers.length > 0
            ? editingTarget.bonusTiers.map((t) => ({
                thresholdDollars: String(t.thresholdCents / 100),
                bonusDollars: String(t.bonusCents / 100),
              }))
            : [{ thresholdDollars: '', bonusDollars: '' }],
        );
        setPeriod(editingTarget.period);
        // For existing targets, respect stored components. Default differs by
        // type when components are absent: commission_earned needs both
        // (measure-driving), others start with neither (metadata-only).
        const comp = editingTarget.commissionComponents;
        if (comp && comp.length > 0) {
          setIncludeUpfront(comp.includes('upfront'));
          setIncludeTrail(comp.includes('trail'));
        } else if (normalizedType === 'commission_earned') {
          setIncludeUpfront(true);
          setIncludeTrail(true);
        } else {
          setIncludeUpfront(false);
          setIncludeTrail(false);
        }
        setSelectedLoanBookId(editingTarget.loanBookId || 'all');
        // Assistant- or analyst-bound targets surface the binding id so the
        // picker renders pre-selected. Non-bound targets default to 'broker'.
        setSelectedAssistantUserId(
          (editingTarget as PerformanceTarget & { assistantUserId?: string }).assistantUserId || '',
        );
        setSelectedAnalystId(
          (editingTarget as PerformanceTarget & { analystId?: string }).analystId || '',
        );
        // Legacy targets with no effectiveFromMonth use current month so
        // the edit form is always valid.
        setEffectiveFromMonth(editingTarget.effectiveFromMonth || currentMonthStr);
      } else {
        setType('loan_book_value');
        setLabel('');
        setTiers([{ thresholdDollars: '', bonusDollars: '' }]);
        setPeriod('monthly');
        setIncludeUpfront(false);
        setIncludeTrail(false);
        setSelectedLoanBookId('all');
        setSelectedAssistantUserId('');
        setSelectedAnalystId('');
        setEffectiveFromMonth(currentMonthStr);
      }
    }
  }, [isOpen, editingTarget, currentMonthStr]);

  // When the admin switches target type in an open modal, adjust checkbox
  // defaults: picking commission_earned auto-checks both (measure-driving);
  // picking loan_book / settlement unchecks both (metadata-only, opt-in).
  useEffect(() => {
    if (!isOpen || editingTarget) return;
    if (type === 'commission_earned') {
      setIncludeUpfront(true);
      setIncludeTrail(true);
    } else {
      setIncludeUpfront(false);
      setIncludeTrail(false);
    }
  }, [type, isOpen, editingTarget]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) {
      toast.error('Please enter a target label');
      return;
    }

    // Phase 6 — validate scaled tiers. Each tier must have a positive
    // threshold; bonus is optional (blank → 0, measurement-only). Tier N
    // threshold must strictly exceed tier (N-1).
    const tierData: BonusTier[] = [];
    for (let i = 0; i < tiers.length; i++) {
      const thresholdNum = parseFloat(tiers[i].thresholdDollars);
      const bonusTrimmed = tiers[i].bonusDollars.trim();
      const bonusNum = bonusTrimmed === '' ? 0 : parseFloat(bonusTrimmed);
      if (isNaN(thresholdNum) || thresholdNum <= 0) {
        toast.error(`Tier ${i + 1}: please enter a valid threshold amount`);
        return;
      }
      if (isNaN(bonusNum) || bonusNum < 0) {
        toast.error(`Tier ${i + 1}: bonus amount must be zero or positive`);
        return;
      }
      const thresholdCents = Math.round(thresholdNum * 100);
      if (i > 0 && thresholdCents <= tierData[i - 1].thresholdCents) {
        toast.error(`Tier ${i + 1} threshold must exceed Tier ${i}`);
        return;
      }
      tierData.push({ thresholdCents, bonusCents: Math.round(bonusNum * 100) });
    }

    // Effective-from: YYYY-MM. Required. Enforced by the picker's `min` but
    // revalidate here in case the browser doesn't enforce it.
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(effectiveFromMonth)) {
      toast.error('Please choose a valid activation month');
      return;
    }

    // Collect component selection. For commission_earned at least one must be
    // checked (measure-driving). For other types zero selections is valid
    // (metadata-only tag) — we send undefined in that case.
    let components: CommissionComponent[] | undefined;
    const selected: CommissionComponent[] = [];
    if (includeUpfront) selected.push('upfront');
    if (includeTrail) selected.push('trail');
    if (type === 'commission_earned') {
      if (selected.length === 0) {
        toast.error('Select at least one commission type (upfront or trail)');
        return;
      }
      components = selected;
    } else if (selected.length > 0) {
      components = selected;
    }

    // Resolve loan book selection → templateId/entityCode. "all" = unscoped.
    const resolvedLoanBook = selectedLoanBookId !== 'all'
      ? resolveLoanBookSelection(selectedLoanBookId, loanBooks)
      : null;
    const loanBookEntry = selectedLoanBookId !== 'all'
      ? loanBooks.find((lb) => lb.id === selectedLoanBookId)
      : null;

    setSaving(true);
    try {
      const assistantEntry = selectedAssistantUserId
        ? assistants.find((a) => a.id === selectedAssistantUserId)
        : null;
      const analystEntry = selectedAnalystId
        ? analysts.find((a) => a.id === selectedAnalystId)
        : null;
      // PR 1A-v3: assistant and analyst bindings are mutually exclusive at
      // the wire (backend rejects both). The picker enforces this via a
      // single state slot but defensively guard here too.
      if (assistantEntry && analystEntry) {
        toast.error('A target can be assigned to either an assistant OR a credit analyst, not both.');
        setSaving(false);
        return;
      }
      await onSave({
        targetId: editingTarget?.id,
        type,
        label: label.trim(),
        bonusTiers: tierData,
        period,
        effectiveFromMonth,
        commissionComponents: components,
        ...(loanBookEntry && resolvedLoanBook?.templateId
          ? {
              loanBookId: loanBookEntry.id,
              loanBookTemplateId: resolvedLoanBook.templateId,
              loanBookEntityCode: resolvedLoanBook.entityCode,
              loanBookLabel: loanBookEntry.name,
            }
          : {}),
        ...(assistantEntry
          ? {
              assistantUserId: assistantEntry.id,
              assistantDisplayName: assistantEntry.displayName,
            }
          : {}),
        ...(analystEntry
          ? {
              analystId: analystEntry.id,
              analystDisplayName: analystEntry.displayName,
            }
          : {}),
      });
      onClose();
    } catch {
      // Error handled in onSave
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Modal body scrolls within the modal so header + footer stay pinned.
  // Previously the whole modal overflowed the viewport on short screens
  // and the background scrolled instead — user couldn't reach Create Target.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white">
            {editingTarget ? 'Edit Target' : 'Add Performance Target'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Target Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as TargetTypeOption)}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="loan_book_value">Loan Book Value</option>
              <option value="settlement_volume">Settlement Volume</option>
              <option value="commission_earned">Commission Earned</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {TARGET_TYPE_HINTS[type]}
            </p>
          </div>

          {/* Commission-component checkboxes.
              - commission_earned: DRIVES the measure. At least one required.
              - loan_book_value / settlement_volume: informational tag only —
                records which commission pool the bonus is paid from. Does
                NOT change the threshold measure (book is still loan_amount
                sum; settlement volume is still newly-settled loan amounts). */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              {type === 'commission_earned' ? (
                <>Commission Types <span className="text-gray-500 font-normal">(at least one)</span></>
              ) : (
                <>Bonus paid from <span className="text-gray-500 font-normal">(optional tag)</span></>
              )}
            </label>
            <div className="flex gap-3">
              <label
                className={`flex-1 flex items-center gap-2.5 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                  includeUpfront
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-400'
                    : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-white'
                }`}
              >
                <input
                  type="checkbox"
                  checked={includeUpfront}
                  onChange={(e) => setIncludeUpfront(e.target.checked)}
                  className="w-4 h-4 accent-blue-500"
                />
                <span className="text-sm font-medium">Upfront</span>
              </label>
              <label
                className={`flex-1 flex items-center gap-2.5 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                  includeTrail
                    ? 'bg-purple-600/20 border-purple-500/50 text-purple-400'
                    : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-white'
                }`}
              >
                <input
                  type="checkbox"
                  checked={includeTrail}
                  onChange={(e) => setIncludeTrail(e.target.checked)}
                  className="w-4 h-4 accent-purple-500"
                />
                <span className="text-sm font-medium">Trail</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              {type === 'commission_earned'
                ? includeUpfront && includeTrail
                  ? 'Threshold sums upfront + trail commission in the period.'
                  : includeUpfront
                  ? 'Threshold measures upfront commission only.'
                  : includeTrail
                  ? 'Threshold measures trail commission only.'
                  : 'Select at least one commission type.'
                : includeUpfront && includeTrail
                ? 'Bonus will be paid from both upfront and trail commission pools.'
                : includeUpfront
                ? 'Bonus will be paid from the upfront commission pool.'
                : includeTrail
                ? 'Bonus will be paid from the trail commission pool.'
                : 'Optional — leave blank if the payment pool isn\'t tracked.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">Label</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={100}
              placeholder="e.g., Maintain $5M loan book"
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">
                Bonus Tiers <span className="text-gray-500 font-normal">(highest met tier wins)</span>
              </label>
              {tiers.length < 10 && (
                <button
                  type="button"
                  onClick={() =>
                    setTiers((prev) => [...prev, { thresholdDollars: '', bonusDollars: '' }])
                  }
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-blue-600/20 border border-blue-500/40 text-blue-300 hover:bg-blue-600/30 transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add tier
                </button>
              )}
            </div>
            <div className="space-y-2">
              {tiers.map((tier, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-start p-3 rounded-lg bg-gray-900/40 border border-gray-700"
                >
                  <div className="w-7 h-7 rounded-md bg-blue-500/15 border border-blue-500/30 flex items-center justify-center text-blue-300 text-xs font-bold mt-5">
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      Threshold
                    </p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        value={tier.thresholdDollars}
                        onChange={(e) =>
                          setTiers((prev) =>
                            prev.map((t, idx) =>
                              idx === i ? { ...t, thresholdDollars: e.target.value } : t,
                            ),
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder={i === 0 ? '5000000' : 'higher than previous'}
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-7 pr-3 py-2 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                      Bonus paid
                    </p>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        value={tier.bonusDollars}
                        onChange={(e) =>
                          setTiers((prev) =>
                            prev.map((t, idx) =>
                              idx === i ? { ...t, bonusDollars: e.target.value } : t,
                            ),
                          )
                        }
                        min="0"
                        step="0.01"
                        placeholder="0 = measurement only"
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg pl-7 pr-3 py-2 text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  {tiers.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => setTiers((prev) => prev.filter((_, idx) => idx !== i))}
                      className="self-center mt-5 p-1.5 rounded-md text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                      title={`Remove tier ${i + 1}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <div className="w-7" />
                  )}
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Broker is paid the bonus from the <strong>highest-threshold tier</strong> they've met.
              Tiers must be in ascending order. Leave a bonus blank or 0 for a measurement-only tier.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1.5">
              Activate From <span className="text-red-400">*</span>
            </label>
            <input
              type="month"
              value={effectiveFromMonth}
              onChange={(e) => setEffectiveFromMonth(e.target.value)}
              required
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              The target is measured from this month onward. Data from earlier months is excluded. Backdating is allowed — pick a historical month to retroactively measure existing settlements/commissions against the target.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Evaluation Period</label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setPeriod('monthly')}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                  period === 'monthly'
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-400'
                    : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setPeriod('quarterly')}
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                  period === 'quarterly'
                    ? 'bg-blue-600/20 border-blue-500/50 text-blue-400'
                    : 'bg-gray-700 border-gray-600 text-gray-400 hover:bg-gray-600 hover:text-white'
                }`}
              >
                Quarterly
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              Monthly = full calendar month. Quarterly = Australian FY quarters (Q1 Jul–Sep, Q2 Oct–Dec, Q3 Jan–Mar, Q4 Apr–Jun).
            </p>
          </div>

          {/* Loan book scope — only rendered when the company has >=1 loan book. */}
          {loanBooks.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Loan Book <span className="text-gray-500 font-normal">(optional scope)</span>
              </label>
              <select
                value={selectedLoanBookId}
                onChange={(e) => setSelectedLoanBookId(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Loan Books</option>
                {loanBooks.map((lb) => (
                  <option key={lb.id} value={lb.id}>{lb.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {selectedLoanBookId === 'all'
                  ? 'Measure across all loan books (template-wide).'
                  : `Scope the measure to the "${loanBooks.find((lb) => lb.id === selectedLoanBookId)?.name || selectedLoanBookId}" loan book only.`}
              </p>
            </div>
          )}

          {/* PR 1A-v3 (2026-05-13): Subject picker — broker / assistant /
              credit analyst. One control with grouped options. Default
              "Broker" means the target applies to the broker's own (net)
              share. Picking an assistant or analyst measures against that
              recipient's share of the broker's earned commission.
              Backend enforces mutual exclusion; the encoded option value
              ("assistant:<id>" or "analyst:<id>") drives separate state
              slots so the dispatch in handleSubmit stays clean. */}
          {(assistants.length > 0 || analysts.length > 0) && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Assign To <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <select
                value={
                  selectedAssistantUserId
                    ? `assistant:${selectedAssistantUserId}`
                    : selectedAnalystId
                      ? `analyst:${selectedAnalystId}`
                      : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  if (v.startsWith('assistant:')) {
                    setSelectedAssistantUserId(v.slice('assistant:'.length));
                    setSelectedAnalystId('');
                  } else if (v.startsWith('analyst:')) {
                    setSelectedAssistantUserId('');
                    setSelectedAnalystId(v.slice('analyst:'.length));
                  } else {
                    setSelectedAssistantUserId('');
                    setSelectedAnalystId('');
                  }
                }}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Broker (default)</option>
                {assistants.length > 0 && (
                  <optgroup label="Broker Assistants">
                    {assistants.map((a) => (
                      <option key={`assistant:${a.id}`} value={`assistant:${a.id}`}>
                        {a.displayName} — {a.sharePct}% of broker's share
                      </option>
                    ))}
                  </optgroup>
                )}
                {analysts.length > 0 && (
                  <optgroup label="Credit Analysts">
                    {analysts.map((a) => (
                      <option key={`analyst:${a.id}`} value={`analyst:${a.id}`}>
                        {a.displayName} — {a.upfrontSharePct}% upfront / {a.trailSharePct}% trail
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {selectedAssistantUserId
                  ? (() => {
                      const a = assistants.find((x) => x.id === selectedAssistantUserId);
                      return a
                        ? `Target measures ${a.displayName}'s share (${a.sharePct}% of the broker's earned commission).`
                        : 'Target measures the selected assistant\'s share.';
                    })()
                  : selectedAnalystId
                    ? (() => {
                        const a = analysts.find((x) => x.id === selectedAnalystId);
                        return a
                          ? `Target measures ${a.displayName}'s share. Per-payment-type scaling: ${a.upfrontSharePct}% applied to upfront measure, ${a.trailSharePct}% to trail.`
                          : 'Target measures the selected analyst\'s share.';
                      })()
                    : 'Target measures the broker\'s own net share (default).'}
              </p>
            </div>
          )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-700 bg-gray-800 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {saving ? 'Saving...' : editingTarget ? 'Update Target' : 'Create Target'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Tab Component ──────────────────────────────────────────────────────────

interface PerformanceTargetsTabProps {
  companyId: string | undefined;
  /** The broker doc ID currently selected by the parent page. Empty string means "no broker". */
  selectedBrokerDocId: string;
  /** All resolved loan books for this company. Empty array when company has none. */
  loanBooks: ResolvedLoanBook[];
  /** Assistants allocated to the selected broker (from commission_config.
   *  assistantSplits). Empty array when broker has none. */
  assistants: Array<{ id: string; displayName: string; sharePct: number }>;
  /** Credit analysts allocated to the selected broker (PR 1A-v3,
   *  2026-05-13). Empty array when broker has no analyst allocations. */
  analysts?: Array<{
    id: string;
    displayName: string;
    upfrontSharePct: number;
    trailSharePct: number;
  }>;
  /** When true (e.g. founder view-as), all write actions short-circuit with
   *  a toast and the Add / Edit / Delete controls render disabled. Backstops
   *  the legacy isFounder bypass in enforceAuth.ts:350. */
  isReadOnly?: boolean;
}

const PerformanceTargetsTab: React.FC<PerformanceTargetsTabProps> = ({
  companyId,
  selectedBrokerDocId,
  loanBooks,
  assistants,
  analysts = [],
  isReadOnly = false,
}) => {
  const brokerDocId = selectedBrokerDocId || undefined;

  const [showModal, setShowModal] = useState(false);
  const [editingTarget, setEditingTarget] = useState<PerformanceTarget | null>(null);

  /** Month the period targets are measured over. null = the tenant's newest
   *  commission month (the backend default). */
  const [periodMonth, setPeriodMonth] = useState<string | null>(null);

  const {
    targets,
    progress,
    totalBonusPayableCents,
    loading: targetsLoading,
    progressLoading,
    error: targetsError,
    refetchProgress,
    measuredMonth,
    latestDataMonth,
    progressError,
  } = useBrokerTargets(companyId, brokerDocId, periodMonth);

  const history = useBrokerTargetHistory(companyId, brokerDocId);
  const refetchHistory = history.refetch;
  const refetchAll = useCallback(() => {
    refetchProgress();
    refetchHistory();
  }, [refetchProgress, refetchHistory]);

  // A new broker starts back on its tenant's newest data month.
  useEffect(() => {
    setPeriodMonth(null);
  }, [brokerDocId]);

  const nowYm = brisbaneMonthNow();
  // Clamped to now: a future-dated upload must not label every real month
  // "no data yet" while the strip claims data runs past today.
  const rawDataMonth = latestDataMonth ?? history.latestDataMonth;
  const dataMonth = rawDataMonth && rawDataMonth > nowYm ? nowYm : rawDataMonth;
  const pickerOptions = useMemo(() => monthOptions(dataMonth, nowYm), [dataMonth, nowYm]);
  const measuringLatest = !periodMonth || periodMonth === dataMonth;
  const historyStats = useMemo(() => {
    // Active targets only, to sit beside the active-target count above.
    const activeIds = new Set(history.targets.filter((t) => t.isActive).map((t) => t.targetId));
    const judged = history.rows.filter(
      (r) => activeIds.has(r.targetId) && (r.status === 'met' || r.status === 'missed'),
    );
    const first = judged.reduce<string | null>(
      (acc, r) => (acc === null || r.periodStart < acc ? r.periodStart : acc),
      null,
    );
    return { judged: judged.length, met: judged.filter((r) => r.status === 'met').length, first };
  }, [history.rows, history.targets]);

  const targetCards = useMemo(() => {
    return targets.map((t) => {
      const p = progress.find((pr) => pr.targetId === t.id);
      return {
        ...t,
        currentValueCents: p?.currentValueCents ?? 0,
        isMet: p?.isMet ?? false,
        percentComplete: p?.percentComplete ?? 0,
        periodLabel: p?.periodLabel,
        notYetActive: p?.notYetActive ?? false,
        // PR 1A-v5 (2026-05-15): combinedBrokers lives on TargetProgress
        // (derived per-request from cross-broker commission_config scan),
        // NOT on the PerformanceTarget Firestore doc. The merger was
        // silently dropping it — same silent-drop class as PR #726.
        // Badge logic reads target.combinedBrokers; it must flow through.
        combinedBrokers: p?.combinedBrokers,
        // PR 1A-v5.1 (2026-05-15): _crossBrokerSource set on cross-broker
        // shared targets (analyst-bound targets stored under a different
        // funding broker). Read from EITHER the firestore-listener doc
        // (set by the cross-broker effect) OR the progress callable (set
        // by the backend's cross-broker query). Either source is fine.
        _crossBrokerSource:
          (t as { _crossBrokerSource?: string })._crossBrokerSource
          ?? (p as { _crossBrokerSource?: string } | undefined)?._crossBrokerSource,
      };
    });
  }, [targets, progress]);

  const activeTargetCount = targets.length;
  const metTargetCount = targetCards.filter((t) => t.isMet).length;
  const loanBookProgress = progress.find((p) => p.type === 'loan_book_value');
  const currentLoanBookCents = loanBookProgress?.currentValueCents ?? 0;

  const handleSaveTarget = useCallback(
    async (data: {
      targetId?: string;
      type: 'loan_book_value' | 'settlement_volume' | 'commission_earned';
      label: string;
      bonusTiers: BonusTier[];
      period: 'monthly' | 'quarterly';
      commissionComponents?: CommissionComponent[];
      loanBookId?: string;
      loanBookTemplateId?: string;
      loanBookEntityCode?: string;
      loanBookLabel?: string;
      assistantUserId?: string;
      assistantDisplayName?: string;
      analystId?: string;
      analystDisplayName?: string;
      effectiveFromMonth: string;
    }) => {
      if (!companyId || !brokerDocId) return;
      if (isReadOnly) {
        toast.error('View-as is read-only — saving a target is not permitted. Exit view-as to make changes.');
        return;
      }
      try {
        // Mirror legacy fields for any older backend version still parsing them.
        // Current backend prefers bonusTiers; legacy fields are treated as
        // denormalized mirrors. Send both for safety.
        const mirroredThreshold = data.bonusTiers[0]?.thresholdCents ?? 0;
        const mirroredBonus =
          data.bonusTiers[data.bonusTiers.length - 1]?.bonusCents ?? 0;
        const upsert = httpsCallable(functions, 'upsertBrokerTarget');
        await upsert({
          companyId,
          brokerDocId,
          targetId: data.targetId,
          type: data.type,
          label: data.label,
          thresholdCents: mirroredThreshold,
          bonusCents: mirroredBonus,
          bonusTiers: data.bonusTiers,
          period: data.period,
          effectiveFromMonth: data.effectiveFromMonth,
          ...(data.commissionComponents && data.commissionComponents.length > 0
            ? { commissionComponents: data.commissionComponents }
            : {}),
          ...(data.loanBookId && data.loanBookTemplateId
            ? {
                loanBookId: data.loanBookId,
                loanBookTemplateId: data.loanBookTemplateId,
                ...(data.loanBookEntityCode
                  ? { loanBookEntityCode: data.loanBookEntityCode }
                  : {}),
                ...(data.loanBookLabel ? { loanBookLabel: data.loanBookLabel } : {}),
              }
            : {}),
          ...(data.assistantUserId ? { assistantUserId: data.assistantUserId } : {}),
          ...(data.analystId ? { analystId: data.analystId } : {}),
        });
        toast.success(data.targetId ? 'Target updated' : 'Target created');
        setTimeout(() => refetchAll(), 500);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to save target';
        toast.error(message);
        throw err;
      }
    },
    [companyId, brokerDocId, refetchAll, isReadOnly]
  );

  const handleDeactivateTarget = useCallback(
    async (targetId: string) => {
      if (!companyId || !brokerDocId) return;
      if (isReadOnly) {
        toast.error('View-as is read-only — deactivating a target is not permitted. Exit view-as to make changes.');
        return;
      }
      try {
        const deleteTarget = httpsCallable(functions, 'deleteBrokerTarget');
        await deleteTarget({ companyId, brokerDocId, targetId });
        toast.success('Target deactivated');
        refetchHistory();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to deactivate target';
        toast.error(message);
      }
    },
    [companyId, brokerDocId, isReadOnly, refetchHistory]
  );

  const handleEditTarget = useCallback((target: PerformanceTarget) => {
    setEditingTarget(target);
    setShowModal(true);
  }, []);

  const handleAddTarget = useCallback(() => {
    setEditingTarget(null);
    setShowModal(true);
  }, []);

  // Parent page already renders its own "select a broker" / "All Brokers" state,
  // so this tab renders nothing when no specific broker is chosen.
  if (!brokerDocId) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
        <Target className="w-10 h-10 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400 font-medium">Select a specific broker</p>
        <p className="text-gray-500 text-sm mt-1">
          Performance Targets are configured per broker. Choose a broker from the header dropdown
          to manage their targets.
        </p>
      </div>
    );
  }

  if (targetsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
        <p className="text-gray-400 text-sm">Loading performance targets…</p>
      </div>
    );
  }

  if (targetsError) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-red-400 font-medium text-sm">Error loading targets</p>
          <p className="text-red-400/70 text-xs mt-0.5">{targetsError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab action bar */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white text-sm font-semibold uppercase tracking-wider">
            Performance Targets
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            KPI-based bonus targets for this broker. Progress is evaluated against live BigQuery data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetchAll()}
            disabled={progressLoading || history.loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-gray-700 border border-gray-600 text-gray-300 hover:text-white hover:bg-gray-600 transition-colors disabled:opacity-50"
            title="Refresh progress from BigQuery"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${progressLoading || history.loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={handleAddTarget}
            disabled={isReadOnly}
            title={isReadOnly ? 'Read-only in view-as mode' : undefined}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Target
          </button>
        </div>
      </div>

      {/* Data freshness + measured month. Period targets are judged on
          commission statements, which arrive after the month they cover, so
          the page measures the newest month that has data and says so. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-gray-700 bg-gray-800/60 px-4 py-2.5 text-xs">
        <CalendarClock className="w-4 h-4 text-blue-400 flex-shrink-0" />
        {dataMonth ? (
          <span className="text-gray-400">
            Commission data through <span className="text-white font-semibold">{monthLong(dataMonth)}</span>
          </span>
        ) : (
          <span className="text-gray-400">No commission data uploaded yet</span>
        )}
        {dataMonth && dataMonth < nowYm && (
          <span className="text-gray-500">
            {addMonths(dataMonth, 1) === nowYm
              ? `${monthLong(nowYm)} not yet uploaded.`
              : `${monthLong(addMonths(dataMonth, 1))} to ${monthLong(nowYm)} not yet uploaded.`}{' '}
            Upfront for a month&apos;s settlements arrives on a later statement.
          </span>
        )}
        {progressError && (
          <span className="text-red-400">Could not load progress for this month: {progressError}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label htmlFor="targets-measured-month" className="text-gray-500 uppercase tracking-wider text-[0.625rem]">
            Measuring
          </label>
          <select
            id="targets-measured-month"
            value={periodMonth ?? measuredMonth ?? dataMonth ?? nowYm}
            onChange={(e) => setPeriodMonth(e.target.value === dataMonth ? null : e.target.value)}
            className="bg-gray-900 border border-gray-600 rounded-lg text-xs text-white px-2 py-1"
          >
            {pickerOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Active Targets"
          value={String(activeTargetCount)}
          accent="blue"
          detail={
            activeTargetCount === 0
              ? 'No targets set'
              : `${activeTargetCount} target${activeTargetCount !== 1 ? 's' : ''} being tracked`
          }
          icon={<Target className="w-5 h-5" />}
          iconBg="rgba(59, 130, 246, 0.3)"
          iconColor="#60a5fa"
        />
        <StatCard
          label="Targets Met"
          value={`${metTargetCount}/${activeTargetCount}`}
          accent="green"
          detail={
            activeTargetCount === 0
              ? 'No targets to evaluate'
              : historyStats.judged > 0 && historyStats.first
                ? `${historyStats.met} of ${historyStats.judged} past periods met since ${monthLong(historyStats.first)}`
                : metTargetCount === activeTargetCount
                  ? 'All targets achieved'
                  : `${activeTargetCount - metTargetCount} remaining`
          }
          valueColor={
            metTargetCount === activeTargetCount && activeTargetCount > 0 ? 'text-green-400' : 'text-white'
          }
          icon={<Award className="w-5 h-5" />}
          iconBg="rgba(34, 197, 94, 0.3)"
          iconColor="#4ade80"
        />
        <StatCard
          label="Total Bonus Payable"
          value={formatCurrency(totalBonusPayableCents)}
          accent="purple"
          detail={totalBonusPayableCents > 0 ? 'Based on met targets' : 'No bonus earned yet'}
          valueColor={totalBonusPayableCents > 0 ? 'text-green-400' : 'text-white'}
          icon={<DollarSign className="w-5 h-5" />}
          iconBg="rgba(168, 85, 247, 0.3)"
          iconColor="#c084fc"
        />
        <StatCard
          label="Current Loan Book"
          value={formatLargeAmount(currentLoanBookCents)}
          accent="amber"
          detail={currentLoanBookCents > 0 ? 'Active loan book value' : 'No loan book data'}
          icon={<Vault className="w-5 h-5" />}
          iconBg="rgba(245, 158, 11, 0.3)"
          iconColor="#fbbf24"
        />
      </div>

      {/* Target Cards Grid */}
      {targetCards.length === 0 ? (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 text-center">
          <Target className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 font-medium">No active targets</p>
          <p className="text-gray-500 text-sm mt-1">
            Click "Add Target" to create a performance target for this broker
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {targetCards.map((target) => {
            const isMet = target.isMet;
            const borderColor = isMet ? 'border-l-green-500' : 'border-l-amber-500';
            const progressPct = Math.min(target.percentComplete, 100);
            const barColor = isMet ? 'bg-green-500' : 'bg-amber-500';

            return (
              <div
                key={target.id}
                className={`bg-gray-800 border border-gray-700 border-l-4 ${borderColor} rounded-lg p-5`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="p-2 rounded-lg mt-0.5"
                      style={{
                        background: (() => {
                          switch (target.type) {
                            case 'loan_book_value':
                              return 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(245, 158, 11, 0.05))';
                            case 'settlement_volume':
                              return 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(16, 185, 129, 0.05))';
                            case 'commission_earned':
                              // Color follows the component selection:
                              // upfront → blue, trail → purple, both → gradient.
                              if (target.commissionComponents?.length === 1) {
                                return target.commissionComponents[0] === 'upfront'
                                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.05))'
                                  : 'linear-gradient(135deg, rgba(168, 85, 247, 0.2), rgba(168, 85, 247, 0.05))';
                              }
                              return 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(168, 85, 247, 0.2))';
                            default:
                              return 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.05))';
                          }
                        })(),
                      }}
                    >
                      {target.type === 'loan_book_value' ? (
                        <Vault className="w-4 h-4 text-amber-400" />
                      ) : target.type === 'settlement_volume' ? (
                        <Handshake className="w-4 h-4 text-emerald-400" />
                      ) : target.commissionComponents?.length === 1 &&
                        target.commissionComponents[0] === 'trail' ? (
                        <TrendingUp className="w-4 h-4 text-purple-400" />
                      ) : (
                        <HandCoins className="w-4 h-4 text-blue-400" />
                      )}
                    </div>
                    <div>
                      {/* PR 1A-v5.3 (2026-05-15): min-h-[2.75rem] forces a
                          consistent header height across cards so the
                          progress bar y-position aligns horizontally even
                          when one card has a multi-badge title (Combined +
                          Shared from + Credit Analyst) and another has
                          only the analyst badge. REM-first per CLAUDE.md §8. */}
                      <div className="flex items-center gap-2 flex-wrap min-h-[2.75rem]">
                        <p className="text-white font-medium text-sm">{target.label}</p>
                        {/* PR 1A-v4 (2026-05-14): subject binding badge —
                            tells admins at a glance whether the target is for
                            the broker, an assistant, or a credit analyst.
                            Without this, image #10's "$1M" card was
                            indistinguishable from a broker-bound or
                            assistant-bound target. */}
                        {(target as { analystDisplayName?: string }).analystDisplayName && (
                          <span className="px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                            Credit Analyst: {(target as { analystDisplayName?: string }).analystDisplayName}
                          </span>
                        )}
                        {(target as { assistantDisplayName?: string }).assistantDisplayName && (
                          <span className="px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide rounded bg-blue-500/20 text-blue-300 border border-blue-500/40">
                            Assistant: {(target as { assistantDisplayName?: string }).assistantDisplayName}
                          </span>
                        )}
                        {/* PR 1A-v5.1 (2026-05-15): cross-broker source badge.
                            This target lives on another broker's subcollection
                            but is visible here because the analyst is allocated
                            on this broker too. Edit/Deactivate happen on the
                            funding broker. */}
                        {(target as { _crossBrokerSource?: string })._crossBrokerSource && (() => {
                          const src = (target as { _crossBrokerSource?: string })._crossBrokerSource!;
                          const cb = (target as { combinedBrokers?: Array<{ brokerDocId: string; brokerName: string }> }).combinedBrokers;
                          const srcName = cb?.find((b) => b.brokerDocId === src)?.brokerName ?? src;
                          return (
                            <span className="px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                              Shared from: {srcName}
                            </span>
                          );
                        })()}
                        {/* PR 1A-v5 (2026-05-14): combined-brokers badge —
                            credit analyst targets now report COMBINED progress
                            across every broker the analyst is allocated on.
                            Show the broker list inline so admins know the
                            number isn't this-broker-only. */}
                        {(target as { combinedBrokers?: Array<{ brokerName: string }> }).combinedBrokers
                          && (target as { combinedBrokers?: Array<{ brokerName: string }> }).combinedBrokers!.length > 1 && (
                          <span
                            className="px-1.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide rounded bg-emerald-700/30 text-emerald-200 border border-emerald-500/30"
                            title={
                              `Combined progress across: ${
                                (target as { combinedBrokers?: Array<{ brokerName: string; upfrontSharePct: number; trailSharePct: number }> })
                                  .combinedBrokers!
                                  .map((b) => `${b.brokerName} (${b.upfrontSharePct}% upfront / ${b.trailSharePct}% trail)`)
                                  .join(', ')
                              }`
                            }
                          >
                            Combined: {(target as { combinedBrokers?: Array<{ brokerName: string }> })
                              .combinedBrokers!.map((b) => b.brokerName).join(' + ')}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-xs mt-0.5">
                        {TARGET_TYPE_LABELS[target.type] || target.type}
                        {commissionSubtitle(target.commissionComponents) && (
                          <span className="text-gray-600"> • {commissionSubtitle(target.commissionComponents)}</span>
                        )}
                        {target.loanBookLabel && (
                          <span className="text-gray-600"> • {target.loanBookLabel}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-gray-700 text-gray-400 border border-gray-600"
                      title={
                        target.periodLabel ||
                        undefined
                      }
                    >
                      {PERIOD_LABELS[target.period] || target.period}
                    </span>
                    {target.notYetActive ? (
                      <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-blue-500/20 text-blue-300 border border-blue-500/30">
                        Starts {formatEffectiveMonth(target.effectiveFromMonth)}
                      </span>
                    ) : isMet ? (
                      <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-green-500/20 text-green-400 border border-green-500/30">
                        Target Met
                      </span>
                    ) : target.type !== 'loan_book_value' &&
                      // Only once this target's own progress has arrived: a just-created
                      // target reads 0 until the refetch lands and must not flash Missed.
                      progress.some((p) => p.targetId === target.id) &&
                      periodClosed(target.period, measuredMonth, dataMonth) ? (
                      // Every month of the period is in and it fell short: a final
                      // result, not progress still to come.
                      <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-red-500/15 text-red-300 border border-red-500/30">
                        Missed
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        In Progress
                      </span>
                    )}
                  </div>
                </div>

                {/* Period window — e.g. "April 2026" or "Q4 (Apr–Jun) 2026" */}
                {target.periodLabel && (
                  <p className="text-[0.625rem] text-gray-600 mb-2 uppercase tracking-wider">
                    Measuring:{' '}
                    {target.type === 'loan_book_value'
                      ? 'live loan book'
                      : `${target.periodLabel}${measuringLatest && dataMonth ? ' (latest data)' : ''}`}
                    {target.effectiveFromMonth && (
                      <span> • Active from {formatEffectiveMonth(target.effectiveFromMonth)}</span>
                    )}
                  </p>
                )}

                <div className="mb-3">
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-gray-400 text-xs">
                      Current:{' '}
                      <span className="text-white font-semibold">
                        {formatLargeAmount(target.currentValueCents)}
                      </span>
                    </span>
                    <span className="text-gray-400 text-xs">
                      Target:{' '}
                      <span className="text-white font-semibold">
                        {formatLargeAmount(target.thresholdCents)}
                      </span>
                    </span>
                  </div>

                  <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden relative">
                    <div
                      className={`h-full ${barColor} rounded-full transition-all duration-500`}
                      style={{ width: `${progressPct}%` }}
                    />
                    {/* Stepped tier markers — vertical lines at each tier
                        threshold, scaled relative to the highest tier. Skip
                        the top-most marker (always at 100% = the right edge). */}
                    {target.bonusTiers.length > 1 &&
                      (() => {
                        const topThreshold =
                          target.bonusTiers[target.bonusTiers.length - 1].thresholdCents;
                        if (topThreshold <= 0) return null;
                        return target.bonusTiers.slice(0, -1).map((tier, i) => {
                          const pct = (tier.thresholdCents / topThreshold) * 100;
                          return (
                            <div
                              key={i}
                              className="absolute top-0 bottom-0 w-0.5 bg-gray-900/80 pointer-events-none"
                              style={{ left: `${pct}%` }}
                            />
                          );
                        });
                      })()}
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-500">
                      {!isMet && !target.notYetActive && target.thresholdCents > target.currentValueCents
                        ? `${formatLargeAmount(target.thresholdCents - target.currentValueCents)} short`
                        : ''}
                    </span>
                    <span className="text-xs text-gray-500">{target.percentComplete.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Recent periods against the threshold (from Target History). */}
                {target.type !== 'loan_book_value' && (() => {
                  const { bars, thresholdPct } = trendBars(history.rows, target.id);
                  if (bars.length < 2) return null;
                  return (
                    <div className="mb-3">
                      <p className="text-[0.625rem] text-gray-500 uppercase tracking-wider mb-1.5">
                        Last {bars.length} {target.period === 'quarterly' ? 'quarters' : 'months'} vs target
                      </p>
                      <div className="relative flex items-end gap-1.5 h-12">
                        <div
                          className="absolute left-0 right-0 border-t border-dashed border-gray-500 pointer-events-none"
                          style={{ bottom: `${thresholdPct}%` }}
                        />
                        {bars.map((b, i) => (
                          <div
                            key={b.periodStart}
                            className={`flex-1 rounded-sm ${
                              b.met ? 'bg-green-500' : i === bars.length - 1 ? 'bg-amber-500' : 'bg-amber-500/60'
                            }`}
                            style={{ height: `${Math.max(b.heightPct, 2)}%` }}
                            title={`${b.periodLabel}: ${formatLargeAmount(b.valueCents)}`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-1.5 mt-1">
                        {bars.map((b) => (
                          <span key={b.periodStart} className="flex-1 text-center text-[0.625rem] text-gray-600">
                            {shortPeriodLabel(b.periodLabel)}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Phase 6 — render the tier list when there's more than one
                    tier. Each row shows met/unmet status. Bonus column on the
                    target card (below) shows the highest met tier's bonus. */}
                {target.bonusTiers.length > 1 && (() => {
                  const progressRow = progress.find((p) => p.targetId === target.id);
                  const metIdx = progressRow?.metTierIndex ?? -1;
                  return (
                    <div className="mt-3 grid gap-1">
                      {target.bonusTiers.map((tier, i) => {
                        const tierMet = i <= metIdx;
                        return (
                          <div
                            key={i}
                            className={`flex items-center justify-between text-xs px-2 py-1 rounded
                              ${tierMet
                                ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                : 'bg-gray-700/40 text-gray-500 border border-transparent'}`}
                          >
                            <span>
                              Tier {i + 1}: {formatLargeAmount(tier.thresholdCents)}
                            </span>
                            <span className="font-mono flex items-center gap-1">
                              {formatCurrencyWithCents(tier.bonusCents)}
                              {tierMet && <CheckCircle2 className="w-3 h-3" />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-green-400" />
                    <span className="text-sm text-gray-400">Bonus:</span>
                    <span
                      className={`text-sm font-semibold ${isMet ? 'text-green-400' : 'text-gray-500'}`}
                    >
                      {formatCurrencyWithCents(target.bonusCents)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(target as { _crossBrokerSource?: string })._crossBrokerSource ? (
                      <span
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-700/20 text-emerald-300 border border-emerald-500/30"
                        title={`This analyst target lives on the funding broker. Edit it from that broker's Performance Targets tab.`}
                      >
                        Shared target — edit from funding broker
                      </span>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditTarget(target)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Read-only in view-as mode' : undefined}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-700 border border-gray-600 text-gray-400 hover:text-white hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Edit2 className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeactivateTarget(target.id)}
                          disabled={isReadOnly}
                          title={isReadOnly ? 'Read-only in view-as mode' : undefined}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-gray-700 border border-gray-600 text-red-400 hover:text-red-300 hover:bg-red-500/10 hover:border-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="w-3 h-3" />
                          Deactivate
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <TargetHistorySection
        // Keyed by broker so its target / year filters reset on a broker switch.
        key={brokerDocId}
        rows={history.rows}
        targets={history.targets}
        skipped={history.skipped}
        omittedRetiredCount={history.omittedRetiredCount}
        loading={history.loading}
        error={history.error}
      />

      {/* Performance Bonus Summary Table */}
      {targetCards.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-700">
            <h3 className="text-white font-semibold text-sm">Performance Bonus Summary</h3>
            <p className="text-gray-500 text-xs mt-0.5">
              All active targets and their current evaluation status
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Target
                  </th>
                  {/* PR 1A-v4.1 (2026-05-14): Subject column — was missing
                      from the summary table so analyst/assistant-bound
                      targets were indistinguishable from broker-bound ones
                      in the table (the cards above already show this). */}
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Subject
                  </th>
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Type
                  </th>
                  <th className="text-left text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Period
                  </th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Threshold
                  </th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Current Value
                  </th>
                  <th className="text-center text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Status
                  </th>
                  <th className="text-right text-gray-400 text-xs font-medium uppercase tracking-wider px-5 py-3">
                    Bonus
                  </th>
                </tr>
              </thead>
              <tbody>
                {targetCards.map((target, idx) => (
                  <tr
                    key={target.id}
                    className={`border-b border-gray-700/50 ${
                      idx % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/50'
                    }`}
                  >
                    <td className="px-5 py-3 text-white font-medium">{target.label}</td>
                    <td className="px-5 py-3 text-xs">
                      {(target as { analystDisplayName?: string }).analystDisplayName ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 w-fit">
                            Credit Analyst: {(target as { analystDisplayName?: string }).analystDisplayName}
                          </span>
                          {(target as { combinedBrokers?: Array<{ brokerName: string }> }).combinedBrokers
                            && (target as { combinedBrokers?: Array<{ brokerName: string }> }).combinedBrokers!.length > 1 && (
                            <span
                              className="text-[0.625rem] text-emerald-200/80 italic"
                              title="Progress combines tagged loans across all brokers the analyst is allocated on"
                            >
                              Combined: {(target as { combinedBrokers?: Array<{ brokerName: string }> })
                                .combinedBrokers!.map((b) => b.brokerName).join(' + ')}
                            </span>
                          )}
                        </div>
                      ) : (target as { assistantDisplayName?: string }).assistantDisplayName ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 border border-blue-500/30">
                          Assistant: {(target as { assistantDisplayName?: string }).assistantDisplayName}
                        </span>
                      ) : (
                        <span className="text-gray-500">Broker</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-400">
                      {TARGET_TYPE_LABELS[target.type] || target.type}
                      {commissionSubtitle(target.commissionComponents) && (
                        <span className="text-gray-600 text-xs block">
                          {commissionSubtitle(target.commissionComponents)}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-gray-700 text-gray-400 border border-gray-600">
                        {PERIOD_LABELS[target.period] || target.period}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right text-gray-300 font-mono">
                      {formatLargeAmount(target.thresholdCents)}
                    </td>
                    <td className="px-5 py-3 text-right text-white font-mono font-semibold">
                      {formatLargeAmount(target.currentValueCents)}
                    </td>
                    <td className="px-5 py-3 text-center">
                      {target.isMet ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-green-500/20 text-green-400 border border-green-500/30">
                          <CheckCircle2 className="w-3 h-3" />
                          Met
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-amber-500/20 text-amber-400 border border-amber-500/30">
                          <Activity className="w-3 h-3" />
                          {target.percentComplete.toFixed(0)}%
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-5 py-3 text-right font-mono font-semibold ${
                        target.isMet ? 'text-green-400' : 'text-gray-500'
                      }`}
                    >
                      {formatCurrencyWithCents(target.bonusCents)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-900/50 border-t border-gray-600">
                  <td
                    colSpan={5}
                    className="px-5 py-3 text-right text-gray-400 font-medium text-xs uppercase tracking-wider"
                  >
                    Total Bonus Payable
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className="text-xs text-gray-500">
                      {metTargetCount}/{activeTargetCount} met
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono font-bold text-green-400">
                    {formatCurrencyWithCents(totalBonusPayableCents)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <TargetModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingTarget(null);
        }}
        onSave={handleSaveTarget}
        editingTarget={editingTarget}
        loanBooks={loanBooks}
        assistants={assistants}
        analysts={analysts}
      />
    </div>
  );
};

export default PerformanceTargetsTab;
